import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ReactComponent as Logo } from '../../assets/logo.svg'
import api from '../../api'
import Button from '../../ui/Button'
import { Alert } from '../../ui'
import { SingleSelect } from '../../ui/form/SingleSelect'
import './Consent.css'
import '../auth/Auth.css'

interface ConsentProject {
    id: number
    name: string
}

interface ConsentSession {
    session_id: string
    client: {
        client_name?: string
        redirect_uri: string
    }
    projects: ConsentProject[]
    scope?: string
}

export default function McpConsent() {
    const { t } = useTranslation()
    const [params] = useSearchParams()
    const sessionId = params.get('session') ?? ''

    const [session, setSession] = useState<ConsentSession | null>(null)
    const [error, setError] = useState<string>('')
    const [project, setProject] = useState<ConsentProject | null>(null)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!sessionId) {
            setError(t('mcp_consent_missing_session'))
            return
        }
        api.mcpAuth.getSession(sessionId).then((s) => {
            setSession(s)
            setProject(s.projects[0] ?? null)
        }).catch((err) => {
            const code = err?.response?.data?.error
            setError(code === 'session_not_found'
                ? t('mcp_consent_expired')
                : t('mcp_consent_load_failed'))
        })
    }, [sessionId, t])

    const decide = async (approve: boolean) => {
        if (!session) return
        if (approve && !project) return
        setSubmitting(true)
        try {
            const { redirect_url } = await api.mcpAuth.decide({
                session: session.session_id,
                project_id: project?.id,
                approve,
            })
            window.location.href = redirect_url
        } catch {
            setError(t('mcp_consent_submit_failed'))
            setSubmitting(false)
        }
    }

    const clientLabel = session?.client.client_name ?? t('mcp_consent_unknown_client')

    return (
        <div className="auth mcp-consent">
            <div className="logo"><Logo /></div>
            <div className="auth-step">
                <h2>{t('mcp_consent_title')}</h2>
                {error && <Alert variant="error" title={t('error')}>{error}</Alert>}
                {session && !error && (
                    <>
                        <p className="mcp-consent-intro">
                            {t('mcp_consent_intro', { client: clientLabel })}
                        </p>
                        <SingleSelect<ConsentProject | null>
                            label={t('mcp_consent_pick_project')}
                            options={session.projects}
                            value={project}
                            onChange={setProject}
                            getOptionDisplay={(p) => p?.name ?? ''}
                            getValueKey={(p) => String(p?.id ?? '')}
                        />
                        <div className="mcp-consent-permissions">
                            <p>{t('mcp_consent_permissions_title')}</p>
                            <ul>
                                <li>{t('mcp_consent_permission_read')}</li>
                                <li>{t('mcp_consent_permission_modify')}</li>
                            </ul>
                        </div>
                        <div className="mcp-consent-actions">
                            <Button
                                variant="secondary"
                                onClick={() => { void decide(false) }}
                                disabled={submitting}
                            >
                                {t('mcp_consent_deny')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => { void decide(true) }}
                                disabled={!project || submitting}
                                isLoading={submitting}
                            >
                                {t('mcp_consent_allow')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
